/**
 * Ship views (plan G6): one model instance per ship entity with its faction look and hardpoint modules.
 *
 * - A view is built from `source.get(def.assetId).instantiate(lodScale)` + `applyShipLook`, with every
 *   `def.mounts[].module` instantiated, given `applyStaticLook` in the ship's faction and parented to its
 *   `HP_` socket. Views are pooled per def: a despawned ship's view goes back to its pool and the next
 *   spawn of that def reuses it. Building happens only when a pool is empty (at spawn or `prewarm`).
 * - M10 P1a1 (`ship-views.json perHullView`): a view follows the model behind its entity. It is released and
 *   re-acquired when its asset id no longer matches its def's (the flown slot after `applyFit`), or when the model
 *   source's `generation` moved and the source now holds another model for that asset. A pooled view is reused only
 *   for its own asset, so switching back to the Kestrel picks up the pooled Kestrel view.
 * - M10 P1b2: boot's source is the ship stream's `getOrStandIn`, so a ship whose GLB isn't in yet shows its band-sized
 *   stand-in (`assets/standIn.ts`), and the generation bump when the GLB lands swaps the real model in place.
 * - Every frame `updateShipViews` places each view at the interpolated pose in render space (D-011),
 *   drives engines, RCS and damage from `Thrust` and `Hull`, and turns `hitShield` / `hitHull` events into
 *   asset-space impacts (the inverse of the interpolated ship transform). Allocation-free (D-014).
 * - PERF PF-5b (D-TBD PERF-8, `materials.json lights.pooled`): hull lights draw from one instanced light pool (one
 *   draw for every ship's nav lights and strobes) instead of a sprite each. A view registers its hull's `LIGHT_`
 *   sockets on acquire and frees them on release (a despawn or a model swap); the pool, a child of `parent` at
 *   identity, is placed from each root's current pose at the end of `updateShipViews` and draws hidden views at
 *   size 0. Hardpoint modules keep their sprites (`applyStaticLook`).
 * - M10 P4b4a: `setKit(eid, kit)` dresses an entity in its operator kit (traffic binds it at promotion): the view wears
 *   the kit's livery and hangs the kit's parts at their mounts (`kitParts.ts`) once both the hull GLB and the operator's
 *   part GLB are loaded (never on a stand-in). The frame loop only compares the wanted kit and the source generation
 *   with the view's; the attach runs on a change (rare).
 */
import { Group, Quaternion, type Mesh, type Object3D } from 'three/webgpu'
import type { LoadedModel, ModelInstance } from '../assets/loader'
import { isStandIn } from '../assets/standIn'
import { EV, EVENT_MASK, firstReadable, type EventRing } from '../combat/events'
import { inverseRotateVec, type Vec3 } from '../core/math'
import type { ShipDef } from '../data/schemas'
import type { Kit } from '../ships/kits'
import { Hull, Life, MAX_ENTITIES, Pos64, PrevPos64, PrevRot, Rot, Ship, Thrust } from '../world/components'
import { toRenderSpace } from './floatingOrigin'
import type { FactionId } from './contracts'
import { attachKitParts, type KitPartInstance } from './kitParts'
import {
  applyShipLook,
  applyStaticLook,
  createLightPool,
  createPlumePool,
  setLook,
  type LightPool,
  type MaterialLibrary,
  type PlumePool,
  type ShipLook,
} from './materials'
import { internals } from './materials/library'
import { loadShipViewsConfig } from './shipViewsConfig'

/** Where views get their models (boot's map by default; the ship stream's `getOrStandIn` from M10 P1b2). */
export interface ShipModelSource {
  get(id: string): LoadedModel | undefined
  /** Bumped when a model may have been replaced (a load or a dispose). */
  readonly generation: number
}

export interface ShipView {
  readonly defIndex: number
  /** The asset the view was built from. */
  readonly assetId: string
  /** The model it was built from (compared with the source's on a generation change). */
  readonly model: LoadedModel
  /** The model's bounding radius (m): the shadow fit. */
  readonly radius: number
  /** The source generation the view was last checked against. */
  gen: number
  readonly instance: ModelInstance
  readonly look: ShipLook
  /** Hardpoint module instances, in `def.mounts` order. */
  readonly modules: readonly ModelInstance[]
  /** The hull's `LIGHT_` sockets drawn from the light pool (empty when `lights.pooled` is off). */
  readonly lights: readonly Object3D[]
  /** The plume pool slots of `look.plumes` while live, -1 while pooled (or when the pool was full; PERF L-M4 part 3). */
  readonly plumeSlots: Int32Array
  /** Their pool slots while live, -1 while pooled (or when the pool was full). */
  readonly lightSlots: Int32Array
  /** The entity shown, -1 while pooled. */
  eid: number
  /** The look the view wears now (its def's faction unless `setLook` dressed its entity in another, M6 P33). */
  worn: FactionId
  /** Interpolated float64 world position of this frame. */
  x: number
  y: number
  z: number
  /** Frame stamp of the last update that saw the entity (despawn detection). */
  stamp: number
  /** M10 P4b4a: the kit the view is dressed for (its parts hang once both GLBs are in), null = none. */
  kit: Kit | null
  /** The kit's hung parts (empty until both GLBs are loaded). */
  readonly parts: KitPartInstance[]
  /** The source generation the kit attach last ran at (-1 = not yet). */
  partsGen: number
}

export interface ShipViewsOptions {
  lib: MaterialLibrary
  models: ReadonlyMap<string, LoadedModel>
  /** The model source; default: `models`, wrapped, at generation 0. Hardpoint modules come from it too. */
  source?: ShipModelSource
  /** Rebuild a view when its entity's model changes (default: `ship-views.json perHullView`). */
  perHullView?: boolean
  defs: readonly ShipDef[]
  /** Where live views are attached (usually the scene). */
  parent: Object3D
  /** LOD distance scale for new instances (quality lodBias). */
  lodScale: number
  /** A hit that removes this fraction of the struck pool's maximum shows at full strength. */
  hitFullStrength: number
}

/** The slice of `Sim` the views read. */
export interface ShipViewSim {
  readonly ships: { readonly dense: ArrayLike<number> }
  readonly events: EventRing
  readonly defs: readonly ShipDef[]
}

export interface ShipViews {
  readonly parent: Object3D
  /** Live views (unordered). */
  readonly active: readonly ShipView[]
  /** Views built so far (live + pooled). */
  readonly created: number
  /** LOD scale for views built from now on. */
  lodScale: number
  /** Event ring read cursor. */
  cursor: number
  frame: number
  /**
   * Test builds (`__debug.soloPlayerShip`, PERF W1 fix): when >= 0, only this entity's view draws, so a fixed perf
   * scene holds no streamed or arriving ship. -1 (the default): every live ship draws.
   */
  soloEid: number
  /** The hull light pool (PERF PF-5b), or null when `materials.json lights.pooled` is off. */
  readonly lights: LightPool | null
  /** The plume pool (PERF L-M4 part 3), or null when `materials.json plumes.pooled` is off. */
  readonly plumes: PlumePool | null
  get(eid: number): ShipView | null
  /** The asset id of entity `eid`'s live view, or null without one (M10 P1a1). */
  assetOf(eid: number): string | null
  /**
   * M6 P33: dresses entity `eid`'s view in `look` instead of its def's faction (the corporation look for own ships),
   * now or when its view is acquired; null goes back to the def's. Forgotten when the entity's view is released
   * because the entity is gone. Rare (a promotion).
   */
  setLook(eid: number, look: FactionId | null): void
  /**
   * M10 P4b4a: dresses entity `eid`'s view in operator kit `kit` (its livery and parts), now or when its view is
   * acquired; null = none. Forgotten when the entity's view is released because the entity is gone. Rare (a promotion).
   */
  setKit(eid: number, kit: Kit | null): void
  /** Builds `count` views of a def into its pool ahead of time. */
  prewarm(defIndex: number, count: number): void
  /** Hit strength 0..1 for `amount` taken out of a pool whose maximum is `max`. */
  strengthOf(amount: number, max: number): number
  dispose(): void
}

interface ShipViewsInternal extends ShipViews {
  readonly source: ShipModelSource
  readonly defs: readonly ShipDef[]
  readonly perHull: boolean
  readonly byEid: (ShipView | null)[]
  readonly lookOf: (FactionId | null)[]
  readonly kitOf: (Kit | null)[]
  readonly lib: MaterialLibrary
  readonly liveViews: ShipView[]
  readonly pools: ShipView[][]
  acquire(defIndex: number, eid: number): ShipView
  release(view: ShipView): void
}

function modelOf(source: ShipModelSource, id: string, shipId: string): LoadedModel {
  const model = source.get(id)
  if (!model) throw new Error(`Ship ${shipId}: model ${id} was not loaded`)
  return model
}

function buildView(opts: ShipViewsOptions, source: ShipModelSource, defIndex: number, lodScale: number, pooled: boolean, pooledPlumes: boolean): ShipView {
  const def = opts.defs[defIndex]
  if (!def) throw new Error(`Unknown ship def index ${defIndex}`)
  const model = modelOf(source, def.assetId, def.id)
  const instance = model.instantiate(lodScale)
  const look = applyShipLook(opts.lib, instance, def.faction, { lightSprites: !pooled, pooledPlumes })
  const lights: Object3D[] = []
  if (pooled) for (const [name, node] of instance.sockets) if (name.startsWith('LIGHT_')) lights.push(node)
  const modules: ModelInstance[] = []
  for (const mount of def.mounts) {
    const socket = instance.sockets.get(mount.socket)
    if (!socket) throw new Error(`Ship ${def.id}: socket ${mount.socket} for ${mount.module} is missing from ${def.assetId}`)
    const module = modelOf(source, mount.module, def.id).instantiate(lodScale)
    applyStaticLook(opts.lib, module, def.faction)
    socket.add(module.root)
    modules.push(module)
  }
  instance.root.name = `ship:${def.id}`
  return {
    defIndex,
    assetId: def.assetId,
    model,
    radius: model.entry.bounds.radius,
    gen: source.generation,
    instance,
    look,
    modules,
    lights,
    lightSlots: new Int32Array(lights.length).fill(-1),
    plumeSlots: new Int32Array(look.plumes.length).fill(-1),
    worn: def.faction,
    eid: -1,
    x: 0,
    y: 0,
    z: 0,
    stamp: -1,
    kit: null,
    parts: [],
    partsGen: -1,
  }
}

/** Boot's model map as a source that never changes. */
function mapSource(models: ReadonlyMap<string, LoadedModel>): ShipModelSource {
  return { get: (id) => models.get(id), generation: 0 }
}

/**
 * True when `view` no longer shows the model behind def `def`: another asset (the flown slot after `applyFit`), or a
 * newer source generation that holds another model for the asset. A generation bump with the same model is noted on
 * the view so the next frame skips the lookup. Allocation-free (D-014).
 */
function isStale(view: ShipView, def: ShipDef, source: ShipModelSource): boolean {
  if (view.assetId !== def.assetId) return true
  if (view.gen === source.generation) return false
  if (source.get(view.assetId) !== view.model) return true
  view.gen = source.generation
  return false
}

/** Index in `pool` of a view of `assetId` that is current in `source`, or -1. Rare (an acquire). */
function findPooled(pool: readonly ShipView[], assetId: string, source: ShipModelSource): number {
  const model = source.get(assetId)
  for (let i = pool.length - 1; i >= 0; i--) {
    if (pool[i].assetId === assetId && pool[i].model === model) return i
  }
  return -1
}

export function createShipViews(opts: ShipViewsOptions): ShipViews {
  const source = opts.source ?? mapSource(opts.models)
  const perHull = opts.perHullView ?? loadShipViewsConfig().perHullView
  const byEid: (ShipView | null)[] = new Array<ShipView | null>(MAX_ENTITIES).fill(null)
  const lookOf: (FactionId | null)[] = new Array<FactionId | null>(MAX_ENTITIES).fill(null)
  const kitOf: (Kit | null)[] = new Array<Kit | null>(MAX_ENTITIES).fill(null)
  const liveViews: ShipView[] = []
  const pools: ShipView[][] = opts.defs.map(() => [])
  const all: ShipView[] = []
  const lightCfg = internals(opts.lib).data.lights
  const lightPool = lightCfg.pooled ? createLightPool(opts.lib, lightCfg.poolCapacity) : null
  if (lightPool) opts.parent.add(lightPool.object)
  const pooled = lightPool !== null
  const plumeCfg = internals(opts.lib).data.plumes
  const plumePool = plumeCfg.pooled ? createPlumePool(opts.lib, plumeCfg.poolCapacity) : null
  if (plumePool) opts.parent.add(plumePool.object)
  const views: ShipViewsInternal = {
    parent: opts.parent,
    active: liveViews,
    get created(): number {
      return all.length
    },
    lodScale: opts.lodScale,
    cursor: 0,
    frame: 0,
    soloEid: -1,
    lights: lightPool,
    plumes: plumePool,
    source,
    defs: opts.defs,
    perHull,
    byEid,
    lookOf,
    kitOf,
    lib: opts.lib,
    liveViews,
    pools,
    get(eid: number): ShipView | null {
      return eid >= 0 && eid < MAX_ENTITIES ? byEid[eid] : null
    },
    assetOf(eid: number): string | null {
      return views.get(eid)?.assetId ?? null
    },
    setLook(eid: number, look: FactionId | null): void {
      if (eid < 0 || eid >= MAX_ENTITIES) return
      lookOf[eid] = look
      const v = byEid[eid]
      if (v !== null) wear(opts.lib, v, look ?? v.kit?.livery ?? opts.defs[v.defIndex].faction)
    },
    setKit(eid: number, kit: Kit | null): void {
      if (eid < 0 || eid >= MAX_ENTITIES) return
      kitOf[eid] = kit
    },
    prewarm(defIndex: number, count: number): void {
      for (let i = 0; i < count; i++) {
        const v = buildView(opts, source, defIndex, views.lodScale, pooled, plumePool !== null)
        all.push(v)
        pools[defIndex].push(v)
      }
    },
    strengthOf(amount: number, max: number): number {
      if (max <= 0) return 1
      const s = amount / (max * opts.hitFullStrength)
      return s > 1 ? 1 : s < 0 ? 0 : s
    },
    acquire(defIndex: number, eid: number): ShipView {
      const pool = pools[defIndex]
      let v: ShipView | undefined
      if (!perHull) v = pool.pop()
      else {
        // A pooled view is reused only for the asset the def shows now (the flown slot changes asset).
        const i = findPooled(pool, opts.defs[defIndex].assetId, source)
        if (i >= 0) {
          v = pool[i]
          pool[i] = pool[pool.length - 1]
          pool.pop()
        }
      }
      if (!v) {
        v = buildView(opts, source, defIndex, views.lodScale, pooled, plumePool !== null)
        all.push(v)
      }
      v.eid = eid
      byEid[eid] = v
      liveViews.push(v)
      opts.parent.add(v.instance.root)
      if (lightPool) for (let k = 0; k < v.lights.length; k++) v.lightSlots[k] = lightPool.add(v.instance.root, v.lights[k])
      if (plumePool) for (let k = 0; k < v.look.plumes.length; k++) v.plumeSlots[k] = plumePool.add(v.instance.root, v.look.plumes[k])
      return v
    },
    release(view: ShipView): void {
      if (view.eid >= 0 && byEid[view.eid] === view) byEid[view.eid] = null
      view.eid = -1
      const i = liveViews.indexOf(view)
      if (i >= 0) {
        liveViews[i] = liveViews[liveViews.length - 1]
        liveViews.pop()
      }
      view.instance.root.removeFromParent()
      dropKit(view)
      if (lightPool) {
        for (let k = 0; k < view.lightSlots.length; k++) lightPool.remove(view.lightSlots[k])
        view.lightSlots.fill(-1)
      }
      if (plumePool) {
        for (let k = 0; k < view.plumeSlots.length; k++) plumePool.remove(view.plumeSlots[k])
        view.plumeSlots.fill(-1)
      }
      // Pooled views start the next life clean.
      view.look.setThrust(0, 0, false)
      view.look.setControl(0, 0, 0, 0, 0)
      view.look.setDamage(1)
      if (perHull && source.get(view.assetId) !== view.model) {
        // Its model was replaced in the source: drop the view instead of pooling it.
        const j = all.indexOf(view)
        if (j >= 0) all.splice(j, 1)
        view.look.dispose()
        return
      }
      view.gen = source.generation
      pools[view.defIndex].push(view)
    },
    dispose(): void {
      for (let i = liveViews.length - 1; i >= 0; i--) views.release(liveViews[i])
      for (let i = 0; i < all.length; i++) all[i].look.dispose()
      all.length = 0
      for (let i = 0; i < pools.length; i++) pools[i].length = 0
      lightPool?.dispose()
      plumePool?.dispose()
    },
  }
  return views
}

/** Repaints `v` (hull, hardpoint modules and kit parts) in `look` when it wears another. Rare: a look change. */
function wear(lib: MaterialLibrary, v: ShipView, look: FactionId): void {
  if (v.worn === look) return
  setLook(lib, v.instance, look)
  for (let i = 0; i < v.modules.length; i++) setLook(lib, v.modules[i], look)
  for (let i = 0; i < v.parts.length; i++) setLook(lib, v.parts[i].instance, look)
  v.worn = look
}

/** Takes `view`'s kit parts off (a release or a kit change). Rare. */
function dropKit(view: ShipView): void {
  for (let i = 0; i < view.parts.length; i++) view.parts[i].holder.removeFromParent()
  view.parts.length = 0
  view.kit = null
  view.partsGen = -1
}

/**
 * M10 P4b4a: brings `view` to kit `kit` (called on a kit change or a source generation bump only): drops another
 * kit's parts, then hangs `kit`'s once the hull model and the operator part GLB are both real (asking the source for
 * the part GLB starts its load). Rare.
 */
function syncKit(v: ShipViewsInternal, view: ShipView, kit: Kit | null): void {
  if (view.kit !== kit) dropKit(view)
  view.kit = kit
  view.partsGen = v.source.generation
  if (kit === null || view.parts.length > 0 || isStandIn(view.model)) return
  const set = v.source.get(kit.partAsset)
  if (set === undefined || isStandIn(set)) return
  const def = v.defs[view.defIndex]
  const hung = attachKitParts(v.lib, view.instance, set, kit, def.id, v.lodScale, view.worn)
  for (let i = 0; i < hung.length; i++) view.parts.push(hung[i])
}

// Scratch (D-014).
const prevQ = new Quaternion()
const curQ = new Quaternion()
const local: Vec3 = { x: 0, y: 0, z: 0 }

function poseView(v: ShipView, eid: number, alpha: number, cam: Vec3): void {
  v.x = PrevPos64.x[eid] + (Pos64.x[eid] - PrevPos64.x[eid]) * alpha
  v.y = PrevPos64.y[eid] + (Pos64.y[eid] - PrevPos64.y[eid]) * alpha
  v.z = PrevPos64.z[eid] + (Pos64.z[eid] - PrevPos64.z[eid]) * alpha
  const root = v.instance.root
  toRenderSpace(v.x, v.y, v.z, cam, root.position)
  prevQ.set(PrevRot.x[eid], PrevRot.y[eid], PrevRot.z[eid], PrevRot.w[eid])
  curQ.set(Rot.x[eid], Rot.y[eid], Rot.z[eid], Rot.w[eid])
  root.quaternion.slerpQuaternions(prevQ, curQ, alpha)
}

function driveLook(v: ShipView, eid: number, def: ShipDef): void {
  const surge = Thrust.surge[eid]
  v.look.setThrust(surge > 0 ? surge : 0, surge < 0 ? -surge : 0, Ship.boosting[eid] === 1)
  v.look.setControl(Thrust.sway[eid], Thrust.heave[eid], Thrust.pitch[eid], Thrust.yaw[eid], Thrust.roll[eid])
  v.look.setDamage(Hull.value[eid] / def.hull)
}

/**
 * Syncs views with the live ships and draws this frame's state. `cam` is the float64 render origin;
 * `hideEid` is an entity whose view stays hidden (the player in cockpit view), -1 for none.
 */
export function updateShipViews(views: ShipViews, sim: ShipViewSim, alpha: number, cam: Vec3, hideEid: number): void {
  const v = views as ShipViewsInternal
  const stamp = ++v.frame
  const dense = sim.ships.dense
  const solo = v.soloEid

  // 1. Acquire, pose and drive every live ship.
  for (let i = 0; i < dense.length; i++) {
    const eid = dense[i]
    const defIndex = Ship.defIndex[eid]
    let view = v.byEid[eid]
    if (view && (view.defIndex !== defIndex || (v.perHull && isStale(view, sim.defs[defIndex], v.source)))) {
      v.release(view)
      view = null
    }
    view ??= v.acquire(defIndex, eid)
    const kit = v.kitOf[eid]
    if (view.kit !== kit || (kit !== null && view.partsGen !== v.source.generation)) syncKit(v, view, kit)
    const want = v.lookOf[eid] ?? (kit !== null ? kit.livery : sim.defs[defIndex].faction)
    if (view.worn !== want) wear(v.lib, view, want)
    view.stamp = stamp
    view.instance.root.visible = Life.alive[eid] === 1 && eid !== hideEid && (solo < 0 || eid === solo)
    poseView(view, eid, alpha, cam)
    driveLook(view, eid, sim.defs[defIndex])
  }

  // 2. Release views whose entity is gone.
  for (let i = v.liveViews.length - 1; i >= 0; i--) {
    const view = v.liveViews[i]
    if (view.stamp !== stamp) {
      if (view.eid >= 0) {
        v.lookOf[view.eid] = null
        v.kitOf[view.eid] = null
      }
      v.release(view)
    }
  }

  // 3. Hits on ships with views, in asset space.
  const ring = sim.events
  for (let seq = firstReadable(ring, v.cursor); seq < ring.head; seq++) {
    const s = seq & EVENT_MASK
    const type = ring.type[s]
    if (type !== EV.hitShield && type !== EV.hitHull) continue
    const amount = ring.amount[s]
    if (amount <= 0) continue
    const view = v.get(ring.eid[s])
    if (!view) continue
    inverseRotateVec(view.instance.root.quaternion, ring.x[s] - view.x, ring.y[s] - view.y, ring.z[s] - view.z, local)
    const def = sim.defs[view.defIndex]
    if (type === EV.hitShield) view.look.shieldHit(local.x, local.y, local.z, v.strengthOf(amount, def.shield))
    else view.look.hit(local.x, local.y, local.z, v.strengthOf(amount, def.hull))
  }
  v.cursor = ring.head

  // 4. Hull lights from each root's pose of this frame (PERF PF-5b; no one-frame lag, review M4).
  v.lights?.update()
  // 5. Plume shells and glows from the same poses and this frame's drive (PERF L-M4 part 3).
  v.plumes?.update()
}


/**
 * M10 P1b2: throwaway stand-ins for the boot pipeline warm pass, so the first stand-in in view builds no pipeline
 * (D-613/D-616). Only what a stand-in adds is warmed: its slot materials. The hull slot's material depends on the
 * look's trim family, so looks are deduped by the hull material they get; engine plumes are already warmed by boot's
 * plume holder and the hull light pool by the player's view, so neither is attached (no sockets are passed to the look).
 * Rare (boot).
 */
export function buildStandInWarmHolder(lib: MaterialLibrary, standIn: LoadedModel, looks: readonly FactionId[], lodScale: number): Group {
  const holder = new Group()
  holder.name = 'standIn:warm'
  const seen = new Set<unknown>()
  const noSockets = new Map<string, Object3D>()
  for (const f of looks) {
    const inst = standIn.instantiate(lodScale)
    applyStaticLook(lib, { root: inst.root, lod: inst.lod, sockets: noSockets }, f)
    let hull: unknown = null
    inst.root.traverse((o) => {
      if (hull === null && o.name === 'standIn:hull') hull = (o as Mesh).material
    })
    if (seen.has(hull)) continue
    seen.add(hull)
    holder.add(inst.root)
  }
  return holder
}
